/**
 * Opta controller live-state slice.
 *
 * Holds the live state of controllers keyed by ctrl_uid: controller status,
 * per-entity state, a rolling event feed, and last command updates. Hydrated
 * from the REST state endpoint and kept live by the WebSocket optaService.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  optaApi,
  ControllerStateResponse,
  ControllerEntityState,
  ControllerEvent,
} from '@/services/optaApi';

interface ControllerLive {
  ctrl_uid: string;
  online: boolean;
  status: string | null;
  fw_rev: string | null;
  ota_state: string | null;
  /**
   * Observed maintenance state: true = outputs dropped, programs stopped, no
   * irrigation. We never set this from having sent a command — a technician at the
   * serial console and the firmware's own OTA both raise it too, so it is only
   * ever mirrored from `status` and MAINTENANCE_ENTER/EXIT events.
   */
  maintenance: boolean;
  last_seen: string | null;
  /**
   * Detected physical I/O as last reported in `status`. Live: a module pulled,
   * restacked or rescanned changes it mid-session, and the terminal pickers must
   * follow. NOT to be confused with the firmware's static `caps`.
   */
  hw_inv: Record<string, any> | null;
  /**
   * Firmware CAPABILITY flags (`status.caps`): `io_state`, `pump_source_band`,
   * `recetas`, `stir_override`… Features are keyed on these and NEVER on
   * `fw_rev` — the firmware team's own rule, and the only thing that survives a
   * unit being flashed out of order. Independent of `hw_inv`.
   */
  caps: Record<string, any> | null;
  /**
   * Messages the controller formed and threw away because its 32-slot outbound
   * queue was full — state, acks and events that NEVER reached the broker. A
   * non-zero `out` means what this UI shows may not be what the equipment has.
   * It resets on reboot, so a RISE is the signal, not the value.
   */
  dropped: { out: number; in: number } | null;
  entities: Record<string, ControllerEntityState>; // key `${tipo}/${id}`
  /**
   * Last cfg/ack per entity, key `${tipo}/${id}`. The controller's verdict on a
   * configuration: whether it applied AND whether it survives a reboot.
   */
  cfgAcks: Record<string, {
    tipo: string; entity_id: number; rev: number; stored_rev: number;
    result: string; persisted: boolean; reason: string | null;
    sync_status: string; received_at: string;
  }>;
  /**
   * Live OTA progress for this controller, from the `ota` block that rides on
   * `opta_status` / `opta_event`. Keyed by nothing — one upgrade at a time per
   * controller is enforced server-side.
   */
  ota: {
    attempt_id: number; device_id: number; state: string;
    reason?: string | null; target_fw_rev: string; from_fw_rev?: string | null;
    confirm_sent: boolean; probation_deadline_at?: string | null;
    finished: boolean; received_at: string;
  } | null;
  /** 'live' | 'disabled' — whether the firmware can actually flash an update. */
  ota_apply: string | null;
  events: ControllerEvent[];
  commands: Record<string, { verb: string; state: string; result?: string | null; reason?: string | null }>;
}

interface OptaState {
  byCtrl: Record<string, ControllerLive>;
  loading: boolean;
  error: string | null;
}

const initialState: OptaState = { byCtrl: {}, loading: false, error: null };

function ensure(state: OptaState, ctrl: string): ControllerLive {
  if (!state.byCtrl[ctrl]) {
    state.byCtrl[ctrl] = {
      ctrl_uid: ctrl, online: false, status: null, fw_rev: null, ota_state: null,
      maintenance: false,
      last_seen: null, hw_inv: null, caps: null, dropped: null,
      entities: {}, cfgAcks: {}, events: [],
      commands: {}, ota: null, ota_apply: null,
    };
  }
  return state.byCtrl[ctrl];
}

export const hydrateControllerState = createAsyncThunk(
  'opta/hydrateState',
  async ({ farmId, claimId }: { farmId: string; claimId: number }) => {
    return optaApi.getState(farmId, claimId);
  },
);

const optaSlice = createSlice({
  name: 'opta',
  initialState,
  reducers: {
    // --- WebSocket-driven updates (from optaService) ---
    optaStatusUpdate(state, action: PayloadAction<{ ctrl_uid: string; online: boolean; fw_rev?: string | null; ota_state?: string | null; maintenance?: boolean; hw_inv?: Record<string, any> | null; caps?: Record<string, any> | null; dropped?: { out: number; in: number } | null }>) {
      const c = ensure(state, action.payload.ctrl_uid);
      c.online = action.payload.online;
      if (action.payload.hw_inv !== undefined) c.hw_inv = action.payload.hw_inv;
      if (action.payload.caps) c.caps = action.payload.caps;
      if (action.payload.dropped) c.dropped = action.payload.dropped;
      c.status = action.payload.online ? 'active' : 'offline';
      if (action.payload.fw_rev !== undefined) c.fw_rev = action.payload.fw_rev;
      if (action.payload.ota_state !== undefined) c.ota_state = action.payload.ota_state;
      if (action.payload.maintenance !== undefined) c.maintenance = action.payload.maintenance;
    },
    optaEntityStateUpdate(state, action: PayloadAction<{ ctrl_uid: string; tipo: string; entity_id: number; state: Record<string, any> }>) {
      const c = ensure(state, action.payload.ctrl_uid);
      const key = `${action.payload.tipo}/${action.payload.entity_id}`;
      c.entities[key] = {
        tipo: action.payload.tipo,
        entity_id: action.payload.entity_id,
        state: action.payload.state,
        ts: (action.payload.state?.ts ?? null) as any,
        updated_at: new Date().toISOString(),
        // The WS payload is the raw firmware state, so every field the BACKEND
        // derived has to be carried forward explicitly or it blinks out between
        // polls. `mode` was the first; `name`/`binding` come from the compiled
        // cfg, and losing `binding` mid-poll would flip a remote sector's row
        // back to the local rendering — the one with the meaningless on/off
        // chip and the buttons that reach no output.
        mode: c.entities[key]?.mode ?? null,
        name: c.entities[key]?.name ?? null,
        binding: c.entities[key]?.binding ?? null,
        // `rules` is the pump -> restriction join the SERVER assembles; the raw
        // firmware state has no idea it exists. Without carrying it, the "why is
        // this pump not running" block under a pump would blink out on every WS
        // tick and reappear only on the 12 s poll — visible, blank, and wrong at
        // exactly the moment somebody is watching it.
        rules: c.entities[key]?.rules ?? null,
        // `reason` DOES come in the firmware state, so it is taken from the new
        // payload rather than carried — carrying it would pin a stale
        // `restricted` badge after the restriction started allowing again.
        reason: (action.payload.state?.reason ?? null) as any,
      };
    },
    /**
     * OTA progress, from the `ota` block on a status or an OTA_* event. The
     * firmware panel renders this directly so an upgrade is watchable live
     * (download → apply → probation → confirmed) rather than by polling.
     */
    optaOtaUpdate(state, action: PayloadAction<{
      ctrl_uid: string; ota_apply?: string | null; ota?: Record<string, any> | null;
    }>) {
      const c = ensure(state, action.payload.ctrl_uid);
      if (action.payload.ota_apply !== undefined) {
        c.ota_apply = action.payload.ota_apply ?? null;
      }
      const ota = action.payload.ota;
      if (ota) {
        c.ota = {
          attempt_id: ota.attempt_id,
          device_id: ota.device_id,
          state: String(ota.state),
          reason: ota.reason ?? null,
          target_fw_rev: String(ota.target_fw_rev ?? ''),
          from_fw_rev: ota.from_fw_rev ?? null,
          confirm_sent: !!ota.confirm_sent,
          probation_deadline_at: ota.probation_deadline_at ?? null,
          finished: !!ota.finished,
          received_at: new Date().toISOString(),
        };
      } else if (ota === null) {
        // An explicit null means the server has no open attempt for this
        // controller any more. Keeping the last block instead — which is what
        // ignoring null did — pinned the firmware panel to the final live state
        // FOREVER: a unit that had rolled back still read "En probación /
        // confirmación enviada", over the top of the fetched row that knew
        // better, until a manual reload wiped the store. `undefined` still means
        // "this message says nothing about OTA", so events keep their block.
        c.ota = null;
      }
    },
    /**
     * The controller's verdict on one cfg. Stored per entity so the cabezal can
     * refetch sync-status the moment an ack lands, instead of waiting for a poll.
     */
    optaCfgAck(state, action: PayloadAction<{
      ctrl_uid: string; tipo: string; entity_id: number; rev: number;
      stored_rev: number; result: string; persisted: boolean;
      reason?: string | null; sync_status: string;
    }>) {
      const c = ensure(state, action.payload.ctrl_uid);
      const { tipo, entity_id } = action.payload;
      c.cfgAcks[`${tipo}/${entity_id}`] = {
        tipo, entity_id,
        rev: action.payload.rev,
        stored_rev: action.payload.stored_rev,
        result: action.payload.result,
        persisted: action.payload.persisted,
        reason: action.payload.reason ?? null,
        sync_status: action.payload.sync_status,
        received_at: new Date().toISOString(),
      };
    },
    optaEventReceived(state, action: PayloadAction<{ ctrl_uid: string; maintenance?: boolean } & ControllerEvent>) {
      const c = ensure(state, action.payload.ctrl_uid);
      const { maintenance, ...evt } = action.payload;
      c.events.unshift({ ...evt, created_at: new Date().toISOString() });
      c.events = c.events.slice(0, 50);
      // MAINTENANCE_ENTER/EXIT carry the resulting flag so the badge flips at once
      // instead of waiting for the controller's next retained status.
      if (maintenance !== undefined) c.maintenance = maintenance;
    },
    optaCommandUpdate(state, action: PayloadAction<{ ctrl_uid: string; req_id: string; verb: string; state: string; result?: string | null; reason?: string | null }>) {
      const c = ensure(state, action.payload.ctrl_uid);
      c.commands[action.payload.req_id] = {
        verb: action.payload.verb, state: action.payload.state,
        result: action.payload.result, reason: action.payload.reason,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateControllerState.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(hydrateControllerState.fulfilled, (state, action: PayloadAction<ControllerStateResponse>) => {
        state.loading = false;
        const p = action.payload;
        const c = ensure(state, p.ctrl_uid);
        c.online = p.online; c.status = p.status; c.fw_rev = p.fw_rev;
        c.ota_state = p.ota_state; c.last_seen = p.last_seen;
        c.maintenance = !!p.maintenance;
        // The REST payload's `caps` is the whole stored blob — capability flags,
        // detected hardware and the dropped-message counters all live in it under
        // separate keys. Unpacked here so no consumer has to know that.
        c.caps = (p.caps as any)?.caps ?? null;
        c.hw_inv = (p.caps as any)?.hw_inv ?? c.hw_inv;
        c.dropped = (p.caps as any)?.dropped ?? null;
        c.entities = {};
        for (const e of p.entities) c.entities[`${e.tipo}/${e.entity_id}`] = e;
        c.events = p.events;
      })
      .addCase(hydrateControllerState.rejected, (state, action) => {
        state.loading = false; state.error = action.error.message || 'Failed to load controller state';
      });
  },
});

export const {
  optaStatusUpdate, optaEntityStateUpdate, optaEventReceived, optaCommandUpdate,
  optaCfgAck, optaOtaUpdate,
} = optaSlice.actions;
export default optaSlice.reducer;
